import { A } from '@solidjs/router';
import type { JSX } from 'solid-js';
import { For, Show } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import { useRepoOverview, useRepoPendingPrs } from '../lib/repo-detail';
import { UNSET } from '../lib/unset';
import { ActivityRow } from './ActivityRow';
import { CardGridSkeleton } from './CardGridSkeleton';
import { EmptyState } from './EmptyState';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { RepoStat } from './RepoStat';
import { buttonClass } from './ui/Button';

// Repo detail → Overview (#195): a stat band (pending / active sessions) over a
// recent-activity feed. Stats come from /overview + the PRs list; each activity
// row links into its full /audit/:session_id timeline.

export type RepoTabProps = {
  readonly encoded: string;
  readonly token: string | null;
};

export function RepoOverviewTab(props: RepoTabProps): JSX.Element {
  const overview = useRepoOverview(() => props.encoded, props.token);
  // The server-filtered pending list (open + `ask`, plan 101 slice 17 row 17-2):
  // page one's rows are the count, and a further page says "N+", never a total
  // it does not hold.
  const prs = useRepoPendingPrs(() => props.encoded, props.token);
  const pending = (): string =>
    prs.hasMore()
      ? t('repo_detail.pending_count_more', { count: prs.rows().length })
      : String(prs.rows().length);

  return (
    <QueryBoundary
      query={overview}
      loading={<CardGridSkeleton listClass="metric-grid" count={2} />}
    >
      {(o) => (
        <div class="repo-detail__stack">
          <ul class="metric-grid" aria-label={t('repo_detail.overview_stats_aria')}>
            <RepoStat
              label={t('repo_detail.stat_pending')}
              // Placeholder until /prs actually resolves — a failed fetch must
              // not render as a confident "0" on a trust-sensitive count (#357).
              value={prs.view.isLoading || prs.view.isError ? UNSET : pending()}
            />
            <RepoStat
              label={t('repo_detail.stat_active_sessions')}
              value={String(o.active_sessions)}
            />
          </ul>

          <Panel
            title={t('repo_detail.activity_title')}
            action={
              <A
                class={buttonClass({ variant: 'link' })}
                href={orgHref(`/repos/${props.encoded}/audit`)}
              >
                {t('repo_detail.activity_view_all')}
              </A>
            }
          >
            <Show
              when={o.recent_activity.length > 0}
              fallback={<EmptyState message={t('repo_detail.sessions_empty')} />}
            >
              <ol class="timeline">
                <For each={o.recent_activity}>{(a) => <ActivityRow row={a} />}</For>
              </ol>
            </Show>
          </Panel>
        </div>
      )}
    </QueryBoundary>
  );
}
